import { createListenerMiddleware } from "@reduxjs/toolkit";
import { updateLocationQuery } from "../router/actions";
import { LOCATION_CHANGE } from "../router/routerAdapter";
import {
  setNewSearch,
  updateAdditionalQueryParams,
} from "../search/domains/store/actionCreators";
import { draftChanged, draftDiscarded } from "./slice";
import { applyFacetDraft } from "./commit";
import { COMMIT_POLICY } from "./commitPolicy";
import {
  selectFacetQuery,
  selectHasPendingFacetChanges,
  selectVisibleFacets,
} from "./selectors";
import { syncSavedFilters } from "../../../client/filterSessionStorage";
import { isSaveFiltersExperimentEnabled } from "../../../client/experimentSaveFilters";

const DEBOUNCE_MS = 250;

/**
 * Built per store rather than shared, so a debounce in one store cannot cancel
 * a debounce in another.
 */
export const createFacetListeners = () => {
  const listeners = createListenerMiddleware();

  listeners.startListening({
    actionCreator: draftChanged,
    effect: async (action, listenerApi) => {
      // Cancels any debounce still waiting, so only the last edit commits.
      listenerApi.cancelActiveListeners();

      const policy = action.meta?.commit;

      if (policy === COMMIT_POLICY.DEBOUNCE) {
        await listenerApi.delay(DEBOUNCE_MS);
      } else if (policy !== COMMIT_POLICY.NOW) {
        return;
      }

      if (selectHasPendingFacetChanges(listenerApi.getState())) {
        listenerApi.dispatch(applyFacetDraft());
      }
    },
  });

  listeners.startListening({
    type: LOCATION_CHANGE,
    effect: (_action, listenerApi) => {
      listenerApi.cancelActiveListeners();
    },
  });

  listeners.startListening({
    actionCreator: applyFacetDraft,
    effect: (_action, listenerApi) => {
      const state = listenerApi.getState();
      const query = selectFacetQuery(state);

      listenerApi.dispatch(setNewSearch());
      listenerApi.dispatch(updateLocationQuery({ query }));
      listenerApi.dispatch(updateAdditionalQueryParams(query));
      listenerApi.dispatch(draftDiscarded());

      // Persist the shopper's deliberate filter change. removeUnapplied defaults
      // to true here so deselecting a filter or clearing all also removes it
      // from storage; plain navigation keeps carry-over filters instead.
      if (isSaveFiltersExperimentEnabled()) {
        syncSavedFilters({
          refine: query.refine,
          facets: selectVisibleFacets(state),
        });
      }
    },
  });

  return listeners;
};
